import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { VOLUNTEER_CATEGORIES } from '../constants/volunteerCategories';
import { useGroups } from '../hooks/useGroups';
import { useVolunteerCategories } from '../hooks/useVolunteerCategories';
import { useVolunteerOpportunities } from '../hooks/useVolunteerOpportunities';
import { useUpsertVolunteerOpportunity } from '../hooks/useUpsertVolunteerOpportunity';
import { supabase } from '../services/supabase';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const FormCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  max-width: 700px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Textarea = styled.textarea`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Select = styled.select`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
`;

const Button = styled.button`
  background-color: var(--primary);
  color: #fff;
  padding: 0.6rem 1.1rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  box-shadow: 0 10px 18px rgba(31, 117, 254, 0.25);
  &:disabled {
    opacity: 0.6;
    cursor: default;
    box-shadow: none;
  }
`;

export const VolunteerFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();

  const { data: opportunities } = useVolunteerOpportunities();
  const existing = opportunities?.find((o) => o.id === id);
  const { data: groups, isLoading: groupsLoading } = useGroups();
  const { data: volunteerCategories } = useVolunteerCategories();
  const categories =
    volunteerCategories && volunteerCategories.length > 0
      ? volunteerCategories
          .filter((cat) => cat.is_active)
          .map((cat) => ({ id: cat.key, label: cat.label }))
      : VOLUNTEER_CATEGORIES;

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [timeCommitment, setTimeCommitment] = useState('');
  const [description, setDescription] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [notificationGroupId, setNotificationGroupId] = useState('none');

  const groupOptions = useMemo(() => {
    const allGroups = groups ?? [];
    const ministryGroups = allGroups.filter((group) => group.category === 'ministry');
    const source = ministryGroups.length > 0 ? ministryGroups : allGroups;
    return [...source].sort((a, b) => a.name.localeCompare(b.name));
  }, [groups]);

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setCategory(existing.category ?? '');
      setLocation(existing.location ?? '');
      setTimeCommitment(existing.time_commitment ?? '');
      setThumbnailUrl(existing.thumbnail ?? '');
      setDescription(existing.description ?? '');
      setNotificationGroupId(existing.notification_group_id ?? 'none');
    }
  }, [existing]);

  const upsert = useUpsertVolunteerOpportunity();

  const handleUpload = async (file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage.from('post-images').upload(fileName, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    setThumbnailUrl(data.publicUrl);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await upsert.mutateAsync({
      id,
      title,
      description: description || undefined,
      category: category || undefined,
      location: location || undefined,
      time_commitment: timeCommitment || undefined,
      thumbnail: thumbnailUrl || undefined,
      notification_group_id: notificationGroupId === 'none' ? null : notificationGroupId,
    });
    navigate('/volunteers');
  };

  return (
    <Page>
      <h1 style={{ marginBottom: '1rem' }}>{editing ? 'Edit' : 'New'} Volunteer Opportunity</h1>
      <FormCard>
        <Form onSubmit={handleSubmit}>
        <label>
          Title
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </label>
        <label>
          Category
          <Input
            list="volunteer-category-options"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Select or type a category"
          />
          <datalist id="volunteer-category-options">
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.label}
              </option>
            ))}
            {category && !categories.some((cat) => cat.id === category) && (
              <option value={category}>{category}</option>
            )}
          </datalist>
        </label>
        <label>
          Location
          <Input value={location} onChange={(e) => setLocation(e.target.value)} />
        </label>
        <label>
          Time Commitment
          <Input value={timeCommitment} onChange={(e) => setTimeCommitment(e.target.value)} />
        </label>
        <label>
          Notification group
          <Select
            value={notificationGroupId}
            onChange={(e) => setNotificationGroupId(e.target.value)}
            disabled={groupsLoading || upsert.isPending}
          >
            <option value="none">None (Discord only)</option>
            {groupOptions.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </Select>
        </label>
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt="Volunteer opportunity thumbnail preview"
            style={{ width: '100%', objectFit: 'cover', borderRadius: 6 }}
          />
        )}
        <label>
          Upload Image
          <Input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              if (e.target.files?.[0]) {
                try {
                  await handleUpload(e.target.files[0]);
                } catch (err) {
                  // eslint-disable-next-line no-alert
                  alert('Upload failed');
                }
              }
            }}
          />
        </label>
        <label>
          Description
          <Textarea value={description} rows={4} onChange={(e) => setDescription(e.target.value)} />
        </label>
          <Button type="submit" disabled={upsert.isPending}>
            {upsert.isPending ? 'Saving…' : 'Save'}
          </Button>
        </Form>
      </FormCard>
    </Page>
  );
};

export default VolunteerFormPage;
